import moment from "moment";

import { PredefinedAppointment } from "@/Appointments/Api/Dto/Responses/PredefinedAppointment";
import { AppointmentCreateData } from "@/Appointments/Models/Appointment/AppointmentCreateDataComposite";
import { ServiceDefinitionWithItsRelatedCombos } from "@/Appointments/Models/Scheduling/ComboServiceModel";
import { CalendarDisplay } from "@/Appointments/Models/Settings/CalendarDisplay";
import { IAppointmentEditModel } from "@/Appointments/Models/UI/IAppointmentEditModel";
import { ICalendarDisplaySettingsModel } from "@/Appointments/Models/UI/ICalendarDisplaySettingsModel";
import { Appointment as MyleModelsResponseCalendarAppointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { EnumAppointmentSource } from "@/Common/Models/Appointments/EnumAppointmentSource";
import { Guid } from "@/Common/Models/Guid";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ICalendarModel } from "../../Models/UI/ICalendarModel";
import { CalendarStore } from "../CalendarStore";

class CalendarAction extends BaseAction {
    private helper: MyleHelper;

    constructor() {
        super();
        this.helper = new MyleHelper();
    }

    public LoadCalendar(calendarModel: ICalendarModel, calendarDisplayModel: ICalendarDisplaySettingsModel, subOperation?: number, employeeIds: string[] = null): void {
        if (calendarModel.get("IsCalendarMonthView"))
            return;

        subOperation ??= SubServerOperations.Calendar.SearchAppointments_GeneralReload;

        const showOnlySelectedSites = calendarDisplayModel.get("ShowOnlySelectedSites");
        const siteIds: string[] = showOnlySelectedSites
            ? this.helper.GetCheckedItemsFromICheckableList(calendarDisplayModel.get("Sites")).toArray().filter(x => x.Checked).map(x => x.Id)
            : calendarDisplayModel.get("Sites").toArray().map(x => x.Id);

        const requestData = {
            Start: MyleHelper.GetStandardizedDateString(calendarModel.get("CalendarStartDate")),
            End: MyleHelper.GetStandardizedDateString(calendarModel.get("CalendarEndDate")),
            IncludeContactPoint: calendarDisplayModel.get("ContactPointsAreVisible"),
            IncludeCancelled: calendarDisplayModel.get("IncludeCanceled"),
            SitesId: siteIds,

            // only fetch appointments that are to be displayed, not for all selected employees
            RelatedEmployees: employeeIds ?? calendarModel.get("DisplayedEmployees").toArray().map(x => x.ContactFacetId),
            RelatedResources: this.helper.GetCheckedItemsFromICheckableList(calendarDisplayModel.get("RoomsFiltered")).toArray().filter(x => x.Checked).map(x => x.Id),
            IncludePredefinedAppointments: true
        };

        // Throlle
        if (calendarStore.timeoutReferenceLoadAppointments !== -1) {
            clearTimeout(calendarStore.timeoutReferenceLoadAppointments);
            calendarStore.timeoutReferenceLoadAppointments = -1;
        }

        calendarStore.timeoutReferenceLoadAppointments = window.setTimeout(() => {
            this.Execute(ServerOperations.Server.Myle_SearchAppointments, requestData, subOperation);
        }, 250);
    }

    public LoadCalendarAppointmentCounts(calendarModel: ICalendarModel, calendarDisplayModel: ICalendarDisplaySettingsModel, onComplete?: () => void, forceRefresh: boolean = false): void {
        const sortByScheduleSelectedEmployees = cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting && calendarDisplayModel.get("SortByScheduleSelectedEmployees");

        if ((!calendarModel.get("IsCalendarMonthView") && !sortByScheduleSelectedEmployees)) {
            onComplete();
            return;
        }

        let employeeIds = calendarModel.get("DisplayedEmployees").toArray().map(emp => emp.ContactFacetId);

        if (cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting) {
            employeeIds = calendarModel.get("AllEmployees").toArray().map(x => x.ContactFacetId);
        }

        const showOnlySelectedSites = calendarDisplayModel.get("ShowOnlySelectedSites");
        const isMonthView = calendarModel.get("IsCalendarMonthView");
        let siteIds: string[] = null;

        if (showOnlySelectedSites) {
            siteIds = this.helper.GetCheckedItemsFromICheckableList(calendarDisplayModel.get("Sites")).toArray().filter(x => x.Checked).map(x => x.Id);
        }
        else if (!cacheStore.ClinicSettings.EnableCalendarAppointmentsCountsCaching) {
            siteIds = calendarDisplayModel.get("Sites").toArray().map(x => x.Id);
        }

        const startDate = calendarModel.get("CalendarStartDate");
        const endDate = calendarModel.get("CalendarEndDate");
        const dayOfWeek = moment(startDate).day();
        const startDaysPadding = (dayOfWeek == 0) ? 7 : dayOfWeek;
        const monthStartDate = moment(startDate).add(-startDaysPadding, "day");
        const monthEndDate = moment(monthStartDate).add(42, "day");
        const requestData = {
            StartDate: MyleHelper.GetStandardizedDateString(isMonthView ? monthStartDate.toDate() : startDate),
            EndDate: MyleHelper.GetStandardizedDateString(isMonthView ? monthEndDate.toDate() : endDate),
            EmployeeIds: employeeIds,
            SiteIds: siteIds,
            forceRefresh: forceRefresh
        };

        // Throlle
        if (calendarStore.timeoutReferenceLoadAppointmentsMonth !== -1) {
            clearTimeout(calendarStore.timeoutReferenceLoadAppointmentsMonth);
            calendarStore.timeoutReferenceLoadAppointmentsMonth = -1;
        }
        calendarStore.timeoutReferenceLoadAppointmentsMonth = window.setTimeout(() => {
            this.Execute(ServerOperations.Server.Myle_Appointment_Counts, requestData, null, null, null, null, onComplete);
        }, 250);
    }

    public ReloadCalendarForEmployee(employeeIds: string[], calendarModel: ICalendarModel, calendarDisplayModel: ICalendarDisplaySettingsModel): void {
        this.LoadCalendar(calendarModel, calendarDisplayModel, SubServerOperations.Calendar.SearchAppointments_ModificationReload, employeeIds);
    }

    public ReloadCalendarForAllEmployee(employeeIds: string[], calendarModel: ICalendarModel, calendarDisplayModel: ICalendarDisplaySettingsModel): void {
        this.LoadCalendar(calendarModel, calendarDisplayModel, SubServerOperations.Calendar.SearchAppointments_GeneralReload, employeeIds);
    }

    public CreateNewPatient(patientModel: IPatientCreationModel): void {
        return this.Execute(
            ServerOperations.Server.Myle_CreatePatient,
            PatientHelper.PrepareCreateNewPatientRequest(patientModel)
        );
    }

    public SaveDisplaySettings(calendarDisplayModel: ICalendarDisplaySettingsModel): void {
        var requestData =
        {
            UserLoginId: sessionStore.userId,
            AppointmentSearchSettings: CalendarDisplay.FromModel(calendarDisplayModel)
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_Save_Search_Config, requestData);
    }

    public LoadSelectedAppointment(calendarModel: ICalendarModel): void {
        var requestData = {
            AppointmentId: calendarModel.get("SelectedAppointmentId"),
            IncludeReferal: true
        };

        this.Execute(ServerOperations.Server.Myle_LoadAppointment, requestData);
    }

    public LoadSelectedPredefinedAppointment(calendarModel: ICalendarModel): void {
        var requestData = {
            PredefinedAppointmentId: calendarModel.get("SelectedAppointmentId")
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_LoadPredefinedAppointment, requestData);
    }

    public LoadPatient(appointmentEditModel: IAppointmentEditModel): void {
        var patientId = appointmentEditModel.get("SelectedPatientId");

        if (patientId) {
            const requestData = {
                IndividualId: appointmentEditModel.get("SelectedPatientId"),
                IncludeRelatedRecalls: true,
                IncludeRelationships: true
            };

            this.Execute(ServerOperations.Server.Myle_LoadPatient, requestData);
        }
        else {
            calendarStore.SetDefaultPatientModel();
        }
    }

    public SaveAppointment(store: CalendarStore): void {
        // appointment is not double booked :  proceed with normal create/update logic
        if (store.appointmentEditModel.get("IsNewAppointment"))
            this.CreateAppointment(store);
        else
            this.UpdateAppointment(store);
    }

    public UpdateAppointment(store: CalendarStore, appointmentToSave: MyleModelsResponseCalendarAppointment = undefined): void {
        var appointmentId: string;

        if (!appointmentToSave) {
            var appointment = store.appointmentEditModel.get("Appointment");
            appointmentId = store.appointmentEditModel.get("Appointment").AppointmentId;

            appointmentToSave = new MyleModelsResponseCalendarAppointment();

            // appointment
            appointmentToSave.AppointmentId = appointmentId;
            appointmentToSave.AppointmentItemId = appointment.AppointmentItemId;
            appointmentToSave.AppointmentStateLookupId = store.GetSelectedAppointmentState().LookupID;
            appointmentToSave.AppointmentStateLookupCode = store.GetSelectedAppointmentState().LookupCode;
            appointmentToSave.AppointmentStateValue = cacheStore.GetLocalizedValueByLookupId(store.GetSelectedAppointmentState().LookupID);
            appointmentToSave.AppointmentComboId = appointment.AppointmentComboId;
            appointmentToSave.StartTime = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("StartTime"));
            appointmentToSave.EndTime = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("EndTime"));
            appointmentToSave.EmployeeId = store.appointmentEditModel.get("AppointmentIntervenor").ContactFacetId;
            appointmentToSave.PatientContactId = store.appointmentEditModel.get("SelectedPatientId");
            appointmentToSave.EmployeInactivableId = appointment.EmployeInactivableId;
            appointmentToSave.Notes = store.appointmentEditModel.get("Notes");
            appointmentToSave.AppointmentIntervenorIds = store.appointmentEditModel.get("SelectedIntervenors").toArray().map(x => x.ContactFacetId);
            appointmentToSave.Location = store.appointmentEditModel.get("RoomText");
            appointmentToSave.ServiceDefinitionId = store.appointmentEditModel.get("SelectedSubjectResourceDefinitionId");

            // set the current patient name if user selected a patient, else set whatever the user typed in the input box
            if (store.appointmentEditModel.get("SelectedPatientId")) {
                appointmentToSave.PatientNameString = store.patientCreationModel.get("LastName") + ", " + store.patientCreationModel.get("FirstName");
            }
            else {
                appointmentToSave.PatientNameString = store.appointmentEditModel.get("PatientSearchString");
            }

            // appointmentItem
            appointmentToSave.Subject = store.appointmentEditModel.get("Subject");

            const referralDoctorId = store.appointmentEditModel.get("ReferralDoctorId")?.toString();
            appointmentToSave.ReferralId = referralDoctorId;
            appointmentToSave.ReferralOrganizationRuleId = store.appointmentEditModel.get("ReferralOrganizationRuleId");
            appointmentToSave.ReferralPhone = store.appointmentEditModel.get("ReferralPhoneNumber");
            appointmentToSave.CustomReferralDoctorName = referralDoctorId ? null : store.appointmentEditModel.get("ReferralDoctorSearchString");

            // appointment resources
            appointmentToSave.SiteId = store.appointmentEditModel.get("SelectedSiteId");
            appointmentToSave.FilteredResourceId = store.appointmentEditModel.get("SelectedSubjectServiceId");
            appointmentToSave.RoomId = store.appointmentEditModel.get("SelectedRoomId");
            appointmentToSave.IsPaid = store.appointmentEditModel.get("IsPaid");
            appointmentToSave.IsTeleconsultation = store.appointmentEditModel.get("IsTeleconsultation");
            appointmentToSave.IsPatientNotificationsEnabled = store.appointmentEditModel.get("NotifyPatient");
            appointmentToSave.CanadianTriageAndAcuityScore = store.appointmentEditModel.get("SelectedCanadianTriageAndAcuityScore");

            if (store.appointmentEditModel.get("IsFromPredefinedAppointment")) {
                var predefinedAppointment: PredefinedAppointment = store.appointmentEditModel.get("PredefinedAppointment");
                appointmentToSave.PredefinedAppointmentId = predefinedAppointment.Id;

                if (appointmentToSave.FilteredResourceId == null || appointmentToSave.FilteredResourceId == "") {
                    let serviceDefinition = cacheStore.GetServiceDefinitionById(predefinedAppointment.ServiceDefinitionId);
                    if (serviceDefinition != null) {
                        let serviceModel: ServiceModel = ArrayHelper.Find(
                            cacheStore.GetEmployeeByContactFacetId(predefinedAppointment.EmployeeId).ServicesOffered,
                            (so: ServiceModel) => so.ServiceDefinitionModel.Model.Id == predefinedAppointment.ServiceDefinitionId
                        );
                        if (serviceModel != null) {
                            appointmentToSave.FilteredResourceId = serviceModel.ServiceID;
                        }
                    }
                }

                // fix for missing patient in print label from predefined appointment
                if (store.appointmentEditModel.get("SelectedPatient").size > 0) {
                    appointmentToSave.PatientNameString = store.appointmentEditModel.get("SelectedPatient").get(0).Name;
                }
            }

            this.SetAdHocServiceOrDefaultIfNeeded(store, appointmentToSave);
            this.SetAppointmentRecurrence(store, appointmentToSave);

            const patient = store.appointmentEditModel.get("SelectedPatient")?.get(0);
            let contactPoints = store.appointmentEditModel.get("ContactPointNotificationList")?.toArray().distinct() ?? [];

            if (patient && store.appointmentEditModel.get("SendToAllPersonalContacts") && !contactPoints.any())
                contactPoints = patient.getAllPersonalContactPointIds();

            const appointmentContactNotification = store.getAppointmentContactPointNotification(patient, contactPoints);

            appointmentToSave.ContactPointNotificationList = appointmentContactNotification.contactPointNotificationList;
            appointmentToSave.HasNotificationContactPointSpecified = appointmentContactNotification.hasNotificationContactPointSpecified;
        }
        else {
            appointmentId = appointmentToSave.AppointmentId;
        }

        appointmentToSave.QuestionnaireStatusInfo = store.appointmentEditModel.get("Questionnaire");

        const requestData = {
            AppointmentId: appointmentId,
            Appointment: appointmentToSave,
            Source: EnumAppointmentSource.Myle_Calendar
        };

        this.Execute(ServerOperations.Server.Myle_UpdateAppointment, requestData);
    }

    public CreateAppointment(store: CalendarStore): void {
        var appointmentId = store.appointmentEditModel.get("Appointment").AppointmentId;
        var appointmentToSave: MyleModelsResponseCalendarAppointment = new MyleModelsResponseCalendarAppointment();

        // appointment
        appointmentToSave.AppointmentId = appointmentId;
        appointmentToSave.AppointmentStateLookupId = store.GetSelectedAppointmentState().LookupID;
        appointmentToSave.AppointmentStateLookupCode = store.GetSelectedAppointmentState().LookupCode;
        appointmentToSave.AppointmentStateValue = cacheStore.GetLocalizedValueByLookupId(store.GetSelectedAppointmentState().LookupID);
        appointmentToSave.EmployeeId = store.appointmentEditModel.get("AppointmentIntervenor").ContactFacetId;

        appointmentToSave.AppointmentIntervenorIds = store.appointmentEditModel.get("SelectedIntervenors").toArray().map(x => x.ContactFacetId);
        appointmentToSave.StartTime = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("StartTime"));
        appointmentToSave.EndTime = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("EndTime"));
        appointmentToSave.PatientContactId = store.appointmentEditModel.get("SelectedPatientId");
        appointmentToSave.Notes = store.appointmentEditModel.get("Notes");
        appointmentToSave.FilteredResourceId = store.appointmentEditModel.get("SelectedSubjectServiceId");
        appointmentToSave.FilteredResourceDefinitionId = store.appointmentEditModel.get("SelectedSubjectResourceDefinitionId");
        appointmentToSave.Location = store.appointmentEditModel.get("RoomText");
        appointmentToSave.RoomString = store.appointmentEditModel.get("RoomText");
        appointmentToSave.IsTeleconsultation = store.appointmentEditModel.get("IsTeleconsultation");
        appointmentToSave.CanadianTriageAndAcuityScore = store.appointmentEditModel.get("SelectedCanadianTriageAndAcuityScore");

        // set the current patient name if user selected a patient, else set whatever the user typed in the input box
        if (store.appointmentEditModel.get("SelectedPatientId")) {
            appointmentToSave.PatientNameString = store.patientCreationModel.get("LastName") + ", " + store.patientCreationModel.get("FirstName");
        }
        else {
            appointmentToSave.PatientNameString = store.appointmentEditModel.get("PatientSearchString");
        }

        // appointmentItem
        appointmentToSave.Subject = store.appointmentEditModel.get("Subject");

        const referralDoctorId = store.appointmentEditModel.get("ReferralDoctorId")?.toString();
        appointmentToSave.ReferralId = referralDoctorId;
        appointmentToSave.ReferralOrganizationRuleId = store.appointmentEditModel.get("ReferralOrganizationRuleId");
        appointmentToSave.ReferralPhone = store.appointmentEditModel.get("ReferralPhoneNumber");
        appointmentToSave.SiteId = store.appointmentEditModel.get("SelectedSiteId");
        appointmentToSave.CustomReferralDoctorName = referralDoctorId ? null : store.appointmentEditModel.get("ReferralDoctorSearchString");

        this.SetAppointmentRecurrence(store, appointmentToSave);

        // appointment resources
        this.SetAdHocServiceOrDefaultIfNeeded(store, appointmentToSave);
        appointmentToSave.RoomId = store.appointmentEditModel.get("SelectedRoomId");
        appointmentToSave.IsPaid = store.appointmentEditModel.get("IsPaid");
        appointmentToSave.IsTeleconsultation = store.appointmentEditModel.get("IsTeleconsultation");
        appointmentToSave.IsPatientNotificationsEnabled = store.appointmentEditModel.get("NotifyPatient");

        if (store.appointmentEditModel.get("IsFromPredefinedAppointment")) {
            var predefinedAppointment: PredefinedAppointment = store.appointmentEditModel.get("PredefinedAppointment");
            appointmentToSave.PredefinedAppointmentId = predefinedAppointment.Id;

            if (appointmentToSave.FilteredResourceId == null || appointmentToSave.FilteredResourceId == "") {
                let serviceDefinition = cacheStore.GetServiceDefinitionById(predefinedAppointment.ServiceDefinitionId);
                if (serviceDefinition != null) {
                    let serviceModel: ServiceModel = ArrayHelper.Find(
                        cacheStore.GetEmployeeByContactFacetId(predefinedAppointment.EmployeeId).ServicesOffered,
                        (so: ServiceModel) => so.ServiceDefinitionModel.Model.Id == predefinedAppointment.ServiceDefinitionId
                    );
                    if (serviceModel != null) {
                        appointmentToSave.FilteredResourceId = serviceModel.ServiceID;
                    }
                }
            }

            // fix for missing patient in print label from predefined appointment
            if (store.appointmentEditModel.get("SelectedPatient").size > 0) {
                appointmentToSave.PatientNameString = store.appointmentEditModel.get("SelectedPatient").get(0).Name;
            }
        }

        let patient = store.appointmentEditModel.get("SelectedPatient")?.toArray()[0];
        let contactPoints = store.appointmentEditModel.get("ContactPointNotificationList")?.toArray().distinct() ?? [];

        if (store.appointmentEditModel.get("SendToAllPersonalContacts") && !contactPoints.any())
            contactPoints = patient.getAllPersonalContactPointIds();

        const appointmentContactNotification = store.getAppointmentContactPointNotification(patient, contactPoints);

        appointmentToSave.ContactPointNotificationList = appointmentContactNotification.contactPointNotificationList;
        appointmentToSave.HasNotificationContactPointSpecified = appointmentContactNotification.hasNotificationContactPointSpecified;

        if (patient) {
            appointmentToSave.PatientNAM = patient.NAM;
            appointmentToSave.PatientFirstName = patient.FirstName;
            appointmentToSave.PatientPreferredName = patient.PreferredName;
            appointmentToSave.PatientLastName = patient.LastName;
        }

        appointmentToSave.QuestionnaireStatusInfo = store.appointmentEditModel.get("Questionnaire");

        const requestData = {
            Appointments: [AppointmentCreateData.FromAppointment(appointmentToSave)],
            forceDoubleBooking: true,
            Source: EnumAppointmentSource.Myle_Calendar,
            RecurrenceType: appointmentToSave.RecurrenceType,
            RecurrenceEndDate: appointmentToSave.RecurrenceEndDate
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_CreateAppointment, requestData);
    }

    public CheckForDoubleBooking(store: CalendarStore): void {
        var startTime = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("StartTime"));
        var endTime = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("EndTime"));
        var duration = moment.duration(moment(endTime).diff(moment(startTime)));

        var durationTicks = duration.asMinutes() * 600000000; // convert duration from minutes to ticks
        var appointmentResourceIds: string[] = [];

        // add the selected room as a resource if applicable
        var roomId = store.appointmentEditModel.get("SelectedRoomId");
        if (roomId)
            appointmentResourceIds.push(roomId);

        // add appointment intervenors
        var selectedIntervenorsId = store.appointmentEditModel.get("SelectedIntervenors").toArray().map(x => x.ContactFacetId);

        var appointmentId = null;
        if (store.appointmentEditModel.get("Appointment").AppointmentId)
            appointmentId = store.appointmentEditModel.get("Appointment").AppointmentId;

        const requestData = {
            AppointmentId: appointmentId,
            AppointmentStartTime: startTime,
            AppointmentDuration: durationTicks,
            AppointmentResourceIds: appointmentResourceIds,
            SelectedIntervenorIds: selectedIntervenorsId
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_CheckDoubleBooking, requestData);
    }

    public CancelAppointment(appointment: MyleModelsResponseCalendarAppointment): void {
        if (appointment != null) {
            const canceledStateLookup = cacheStore.FindLookupByLookupCode("CANCELED");

            const requestData = {
                AppointmentId: appointment.AppointmentId,
                StateLookupId: canceledStateLookup.LookupID,
                Source: EnumAppointmentSource.Myle_Calendar,
                StateCode: canceledStateLookup.LookupCode
            };

            this.Execute(ServerOperations.Server.Myle_Appointment_ChangeStatus, requestData);
        }
    }

    public DeletePredefinedAppointment(predefinedAppointmentId: string): void {
        const requestData = {
            PredefinedAppointmentId: predefinedAppointmentId
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_DeletePredefinedAppointment, requestData);
    }

    public PrintAppointments(store: CalendarStore, fromMDdialog: boolean): void {
        const settingModel = store.calendarDisplaySettingsModel;
        const startDate = store.calendarModel.get("CalendarStartDate");
        const endDate = store.calendarModel.get("CalendarEndDate");

        let selectedEmployeeIds: string[];
        let selectedPrintOption: string;

        if (fromMDdialog) {
            // get selected employees from MDPrintDialog
            selectedEmployeeIds = store.calendarPrintByDoctorModel.get("EmployeesFiltered").toArray().filter(p => p.Checked).map(p => p.Model.ContactFacet_id);
            selectedPrintOption = store.calendarPrintByDoctorModel.get("SelectedPrintOption");
        }
        else {
            // get selected employees from calendar display model
            selectedEmployeeIds = settingModel.get("EmployeesGlobalCache").toArray().filter(p => p.Checked).map(p => p.Model.ContactFacet_id);
            selectedPrintOption = store.calendarPrintDialogModel.get("SelectedPrintOption");
        }

        const requestData = {
            EmployeeIds: selectedEmployeeIds,
            SiteIds: settingModel.get("ShowOnlySelectedSites") === true
                ? settingModel.get("Sites").toArray().filter(p => p.Checked).map(p => p.Id)
                : store.calendarDisplaySettingsModel.get("Sites").toArray().map(p => p.Id),
            StartDate: startDate,
            EndDate: endDate,
            IsPrintByDate: this.IsPrintByDateSelected(selectedPrintOption),
            IsPrintByIdentifier: this.IsPrintByIdentifierSelected(selectedPrintOption),
            IsPrintByPatient: this.IsPrintByPatientSelected(selectedPrintOption),
            IsPrintBorrowingSheet: this.IsPrintBorrowingSheetSelected(selectedPrintOption),
            IsPrintLabelsLabels: this.IsPrintLabelsLabels(selectedPrintOption),
            IncludeCancelled: false,
            IncludeNAM: settingModel.get("IncludeNAM"),
            IncludePatientName: settingModel.get("PatientsAreVisible"),
            IncludePatientPhone: settingModel.get("ContactPointsAreVisible"),
            IncludePatientSite: settingModel.get("SitesAreVisible")
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_GenerateAppointmentsPDF, requestData);
    }

    public PrintAppointmentLabel(employeeId: Guid, patientName: string, startTime: Date, siteId: Guid): void {
        let requestData = this.BuildPrintAppointmentLabelRequestData(employeeId, patientName, startTime, siteId);
        this.Execute(ServerOperations.Server.Myle_Calendar_GenerateAppointmentConfirmationPDF, requestData);
    }

    private BuildPrintAppointmentLabelRequestData(employeeId: Guid, patientName: string, startTime: Date, siteId: Guid): PrintAppointmentLabelRequestData {
        const employee = cacheStore.GetEmployeeByContactFacetId(employeeId);
        const title = LicenceOrganisationRule.getTitleName(Employee.getIssuingOrgRule(employee));
        const employeeString = employee.LastName + ", " + employee.FirstName + " - " + title;
        const appointmentDate = this.helper.CreateDateAsUTC(startTime);

        const site = cacheStore.SiteCache.filter(x => x.Model.Id === siteId)[0];
        const siteString = site.Model.ClinicalName;

        const siteAddress = (site.Model.Adresses[0]) ? site.Model.Adresses[0].StreetAddress + ", " + site.Model.Adresses[0].ZipCode + ", " + site.Model.Adresses[0].City : "";

        const PhoneNumber = (site.Model.Phones && site.Model.Phones.length > 0) ? cacheStore.GetPhoneByType(site.Model.Phones, "PHONE", null, true) : "";
        let sitePhone = "";
        if (!IsNullOrWhiteSpace(PhoneNumber))
            sitePhone = "Tel: " + PhoneNumber;

        return {
            AppointmentDate: appointmentDate,
            PatientNameString: patientName,
            EmployeeNameString: employeeString,
            ClinicNameString: siteString,
            ClinicAddressString: siteAddress,
            ClinicPhoneNumberString: sitePhone,
            GenerateForThermalPrinter: settingsStore.configuration.get("IsNormalConfirmationPrinter") == null ? false : !settingsStore.configuration.get("IsNormalConfirmationPrinter")
        };
    }

    public PrintAppointmentCertificate(patientContactId: string, patientProfileId: string, startTime: Date, siteId: string, patientName?: string): void {
        const requestData = {
            PatientContactId: patientContactId,
            PatientProfileId: patientProfileId,
            AppointmentDate: startTime,
            SiteId: siteId,
            PatientNameString: patientName
        };

        this.Execute(ServerOperations.Server.Myle_AppointmentCertificate_GeneratePdf, requestData);
    }

    public PrintMergeAppointmentConfirmationCertificate(employeeId: Guid, patientName: string, patientContactId: Guid, patientProfileId: string, startTime: Date, siteId: Guid): void {
        let requestData = {
            ...this.BuildPrintAppointmentLabelRequestData(employeeId, patientName, startTime, siteId),
            SiteId: siteId,
            PatientContactId: patientContactId,
            PatientProfileId: patientProfileId
        };
        this.Execute(ServerOperations.Server.Myle_AppointmentConfirmation_AppointmentCertificate_GenerateMergePdf, requestData);
    }

    public CompletePatientRecall(store: CalendarStore, recallId: string): void {
        var requestData = { RecallId: recallId };
        this.Execute(ServerOperations.Server.Myle_SetRecallToCompleted, requestData);
    }

    public GetWorkingClinicians(store: CalendarStore): void {
        var requestData = {
            Start: moment.utc(store.calendarModel.get("CalendarStartDate")).toDate(),
            End: store.calendarModel.get("CalendarEndDate")
        };

        this.Execute(ServerOperations.Server.Myle_Appointment_FindWorkingClinicians, requestData);
    }

    private IsPrintByDateSelected(selectedPrintOption: string): boolean {
        return selectedPrintOption === "Date";
    }

    private IsPrintByIdentifierSelected(selectedPrintOption: string): boolean {
        return selectedPrintOption === "Identifier";
    }

    private IsPrintByPatientSelected(selectedPrintOption: string): boolean {
        return selectedPrintOption === "Patient";
    }

    private IsPrintBorrowingSheetSelected(selectedPrintOption: string): boolean {
        return selectedPrintOption === "BorrowingSheet";
    }

    private IsPrintLabelsLabels(selectedPrintOption: string): boolean {
        return selectedPrintOption === "Labels";
    }

    private SetAdHocServiceOrDefaultIfNeeded(store: CalendarStore, appointment: MyleModelsResponseCalendarAppointment): void {
        if (appointment.FilteredResourceDefinitionId == null && (appointment.FilteredResourceId == null || appointment.FilteredResourceId == "" || appointment.FilteredResourceId == GuidHelper.Empty())) {
            // if no services are selected, set the employee default service (adhoc)
            var employeeServices = cacheStore.GetEmployeeByContactFacetId(appointment.EmployeeId).ServicesOffered;

            var adhocDefaultService = employeeServices.filter(x => x.ServiceDefinitionModel.IsAdHoc == true);
            if (adhocDefaultService && adhocDefaultService.length > 0) {
                appointment.FilteredResourcesServiceName = adhocDefaultService[0].ServiceDefinitionModel.Model.Code;
                appointment.FilteredResourceId = adhocDefaultService[0].ServiceID;
                appointment.FilteredResourceDefinitionId = adhocDefaultService[0].ServiceDefinitionModel.Model.Id;
            }
        }
    }

    private SetAppointmentRecurrence(store: CalendarStore, appointment: MyleModelsResponseCalendarAppointment): void {
        appointment.RecurrenceType = store.appointmentEditModel.get("SelectedRecurrenceType");
        appointment.RecurrenceEndDate = this.helper.CreateDateAsUTC(store.appointmentEditModel.get("RecurrenceEndDate"));
    }

    public LoadRuleset(rulesetId: string): void {
        const requestData = {
            RulesetId: rulesetId
        };

        this.Execute(ServerOperations.Server.Myle_Scheduling_GetRuleset, requestData, SubServerOperations.Calendar.LoadRuleset);
    }

    public CheckIfRulesetIsValid(modifiedServices: ServiceDefinitionWithItsRelatedCombos[], selectedRulesetId: string, then: (isValid: boolean) => void): void {
        const requestData = {
            SelectedRuleSet: modifiedServices,
            RulesetId: selectedRulesetId
        };

        this.dispatch(ServerOperations.Server.Myle_Scheduling_CheckRulesetValidity, requestData, then);
    }

    public UpdateRuleset(
        editedRuleset: ServiceDefinitionWithItsRelatedCombos[],
        selectedRulesetId: string,
        employeeId: string,
        siteIds: string[],
        startDate?: Date,
        endDate?: Date,
        forceDelete: boolean = false,
        isBookingHubAvailability: boolean = false
    ): void {
        var requestData = {
            SelectedRuleSet: editedRuleset,
            RulesetId: selectedRulesetId,
            EmployeeId: employeeId,
            StartDate: startDate ? MyleHelper.GetTimezoneAgnosticDatetimeFormat(startDate) : null,
            EndDate: endDate ? MyleHelper.GetTimezoneAgnosticDatetimeFormat(endDate) : null,
            ForceDelete: forceDelete,
            SiteIds: siteIds,
            IsBookingHubAvailability: isBookingHubAvailability
        };

        this.Execute(ServerOperations.Server.Myle_Scheduling_UpdateRuleSet, requestData);
    }

    public LoadPortalPatientProfile(profileId: string): void {
        var requestData = {
            PatientProfileId: profileId
        };
        this.Execute(ServerOperations.Server.Portal_Proxy_GetPatientProfile, requestData);
    }

    public deleteMyleRuleset(rulesetId: number): void {
        let requestData = {
            Type: 3,
            RuleSetId: rulesetId,
            PredefinedAppointmentId: "" // 2 is predefinedAppointment, 3 is PredefinedRuleSet
        };

        this.Execute(ServerOperations.Server.Myle_Scheduling_DeleteSinglePredefinedAppointmentOrRuleSet, requestData);
    }

    public loadRamqInfo(patient: MyleModelsPatientPatient): void {
        const requestData = {
            Patient: {
                IndividualID: patient.IndividualResponse.Contact_id
            },
            CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
        };

        this.Execute(ServerOperations.Server.RAMQ_CheckSubscription, requestData);
    }

    public LoadAppointmentExternalPatientInfo(patientSnapshotId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Calendar_LoadAppointmentThirdPartyPatientInfo,
            { PatientSnapshotId: patientSnapshotId }
        );
    }
}

interface PrintAppointmentLabelRequestData {
    AppointmentDate: Date,
    PatientNameString: string,
    EmployeeNameString: string,
    ClinicNameString: string,
    ClinicAddressString: string,
    ClinicPhoneNumberString: string,
    GenerateForThermalPrinter: boolean
}

export { CalendarAction };
